import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  Outlet,
  useOutletContext,
} from "react-router-dom";
import {
  TabContent,
  Spinner,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  CloseButton,
} from "reactstrap";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import {
  getSignedToken,
  getOrder,
  getSignedDrillingExportURL,
} from "../project/service";
import {
  getDeliveryDate,
  splitOrder,
  updateOrderStatus,
} from "../project/service";
import { ProjectButton } from "../project/style";
import ConfirmationModal from "../project/ConfirmationModal";
import { BannerContainer, IconContainer, SecondaryButton } from "./style";
import { generateRequestUri } from "../common/service";

const Order = () => {
  const [currentOrder, setCurrentOrder] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [notificationMessage, setNotificationMessage] = useState({});
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationContent, setConfirmationContent] = useState({
    heading: "",
    message: "",
    func: null,
  });
  const [isOrderFinalised, setIsOrderFinalised] = useState(false);
  const [finalisedContexts, setFinalisedContexts] = useState([]);

  const { currentRoom, loadRoom, isAdmin, isDesigner, access, projectSummary } =
    useOutletContext();
  const [currentOrderSection, setCurrentOrderSection] = useState(0);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryModal, setDeliveryModal] = useState(false);

  const toggleDeliveryModal = () => setDeliveryModal(!deliveryModal);
  const toggleConfirmation = () => setConfirmationOpen(!confirmationOpen);

  const showOrderSections = isAdmin;
  const showCutlistSection = isAdmin;

  useEffect(() => {
    loadOrder();
  }, []);

  useEffect(() => {
    if (
      currentOrder &&
      showOrderSections &&
      !location.pathname.includes("cutlist") &&
      !location.pathname.includes("bom")
    )
      navigate(`cutlists`, {
        state: {
          finalisedContexts: finalisedContexts,
        },
      });
  }, [currentOrder]);

  const loadOrder = () => {
    setIsLoading(true);
    getOrder({ projectId: projectSummary.id, orderId: projectSummary.order_id })
      .then((order) => {
        setCurrentOrder(order);
        setIsOrderFinalised(order.is_order_finalised);
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotificationMessage({ type: "error", text: error });
      })
      .finally(() => setIsLoading(false));
  };

  const splitProjectOrder = () => {
    setIsLoading(true);
    splitOrder({ projectId: params.projectId })
      .then(() => {
        navigate(`../orders`);
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotificationMessage({ type: "danger", text: error });
      })
      .finally(() => {
        setIsLoading(false);
        setConfirmationOpen(false);
      });
  };

  const changeOrderStatus = (status) => {
    setIsLoading(true);
    updateOrderStatus({
      projectId: params.projectId,
      orderId: params.orderId,
      status: status,
    })
      .then((order) => {
        setCurrentOrder({
          ...currentOrder,
          ...{ status: order.status },
          ...{ identifier: order.identifier },
        });
        setIsOrderFinalised(order.is_order_finalised);
        setFinalisedContexts(order.finalised_contexts);
        if (["draft", "finalised"].includes(order.status))
          loadRoom(currentRoom.id);
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotificationMessage({ type: "danger", text: error });
      })
      .finally(() => {
        setIsLoading(false);
        setConfirmationOpen(false);
      });
  };

  const getOrderDeliveryDate = () => {
    getDeliveryDate({ projectId: params.projectId, orderId: params.orderId })
      .then(({ delivery_date }) => {
        toggleDeliveryModal();
        setDeliveryDate(delivery_date);
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotificationMessage({ type: "danger", text: error });
      });
  };

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const isOrderPlaced = (status) => {
    return !["draft", "finalised", "ready_to_place"].includes(status);
  };

  const orderSections = [
    {
      url: "../cutlists",
      icon: "fa-solid fa-list-ul",
      tooltip: "Cutlist",
      visible: showCutlistSection,
    },
    {
      url: "../bom",
      icon: "fa-solid fa-indian-rupee-sign",
      tooltip: "BOM",
      visible: showOrderSections,
    },
    {
      url: "../optimization",
      icon: "fa-solid fa-table-list",
      tooltip: "Optimization",
      visible: showOrderSections,
    },
    {
      url: "../panels?rows=8&cols=3&top_margin=11&left_margin=6&row_margin=0&col_margin=3&order_id=0105&client_logo=none",
      icon: "fa-solid fa-tags",
      tooltip: "Label",
      visible: showOrderSections,
    },
  ];

  const placeHolder = {
    move_to_cart: {
      text: "Move to Cart",
      icon: "fa-solid fa-cart-shopping",
      heading: "Move to Cart",
      message: "Are you sure you want to move this order to the cart?",
      func: () => changeOrderStatus("finalised"),
    },
    back_to_draft: {
      text: "Back to Draft",
      icon: "fa-solid fa-arrow-turn-up",
      heading: "Back to Draft",
      message: "Are you sure you want to move back to draft?",
      func: () => changeOrderStatus("draft"),
    },

    ready_to_place: {
      text: "Ready to Place",
      icon: "fa-solid fa-arrow-turn-up",
      heading: "Ready to Place",
      message: "Are you sure you want to mark the order as ready to place?",
      func: () => changeOrderStatus("ready_to_place"),
    },
    back_to_cart: {
      text: "Back to Cart",
      icon: "fa-solid fa-arrow-turn-up",
      heading: "Back to Cart",
      message: "Are you sure you want to move the order back to cart?",
      func: () => changeOrderStatus("finalised"),
    },
    mark_as_delivered: {
      text: "Mark as Delivered",
      icon: "fas fa-shipping-fast",
      heading: "Mark as Delivered",
      message: "Are you sure you want to mark the order as delivered?",
      func: () => changeOrderStatus("delivered"),
    },
  };

  const downloadDrilling = () => {
    getSignedDrillingExportURL({ projectId: params.projectId }).then(
      (signed) => {
        window.open(signed.url, "_blank", "noreferrer");
      }
    );
  };

  const downloadHardwares = () => {
    setIsLoading(true);

    getSignedToken({
      projectId: params.projectId,
      resource: "hardware_download",
    })
      .then(({ token }) =>
        window.open(
          generateRequestUri({
            url: `projects/${params.projectId}/hardware_export`,
            params: { signed: token },
          }),
          "_blank",
          "noreferrer"
        )
      )
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotificationMessage({ type: "danger", text: error });
      })
      .finally(() => setIsLoading(false));
  };

  return isLoading ? (
    <div className="position-relative">
      <CustomizationSpinnerContainer top="200px" left="40%">
        <Spinner />
      </CustomizationSpinnerContainer>
    </div>
  ) : (
    <>
      {notificationMessage.text && (
        <Notification
          color={notificationMessage.type}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notificationMessage.text}
        </Notification>
      )}
      {currentOrder && (
        <div className="pe-4">
          <div className="mt-3">
            <div className="d-flex justify-content-between">
              <div className="d-flex align-items-center column-gap-3">
                {currentOrder.context && (
                  <>
                    <p
                      onClick={() => navigate(`../orders`)}
                      className="m-0 pointer"
                      style={{ color: "#2C92E6" }}
                    >
                      Orders
                    </p>
                    <i
                      className="fa-solid fa-chevron-right"
                      style={{ color: "#2C92E6", marginTop: "0.5px" }}
                    ></i>
                  </>
                )}
                <div>
                  <h5 className="fw-bold m-0">{currentOrder.name}</h5>
                  {currentOrder.identifier && (
                    <span
                      style={{ fontSize: "12px" }}
                      className="fw-bold text-secondary"
                    >
                      {currentOrder.identifier}
                    </span>
                  )}
                </div>
                {
                  <div className="d-flex column-gap-2">
                    {orderSections.map((e, index) => {
                      return (
                        e.visible && (
                          <>
                            <IconContainer
                              key={e.tooltip}
                              id={e.tooltip}
                              className={
                                currentOrderSection == index && "active"
                              }
                              onClick={() => {
                                navigate(`${currentOrder.id}/${e.url}`);
                                setCurrentOrderSection(index);
                              }}
                            >
                              <i className={e.icon}></i>
                            </IconContainer>
                            <UncontrolledTooltip
                              style={{
                                padding: "6px 20px",
                              }}
                              target={e.tooltip}
                              placement="bottom"
                            >
                              {e.tooltip}
                            </UncontrolledTooltip>
                          </>
                        )
                      );
                    })}
                  </div>
                }
              </div>
            </div>
            {!isOrderPlaced(currentOrder.status) && false && (
              <div className="d-flex justify-content-end mt-2">
                <p
                  onClick={() => getOrderDeliveryDate()}
                  className="m-0 pointer"
                  style={{ color: "#2C51E6" }}
                >
                  When can I get this order?
                </p>
              </div>
            )}
            {access.view_drilling && false && (
              <SecondaryButton
                className={"button"}
                onClick={() => downloadDrilling()}
              >
                Download Drilling
              </SecondaryButton>
            )}
            {access.download_hardware && false && (
              <SecondaryButton
                className={"button ms-3"}
                onClick={() => downloadHardwares()}
              >
                Download Hardwares
              </SecondaryButton>
            )}
            {currentOrder.status == "finalised" && (
              <BannerContainer>
                <p className="m-0 fw-bold">
                  Your order is in the cart. Please contact support for placing
                  the order.
                </p>
              </BannerContainer>
            )}
          </div>

          <ConfirmationModal
            isOpen={confirmationOpen}
            toggle={toggleConfirmation}
            onConfirm={confirmationContent["func"]}
            message={confirmationContent["message"]}
            headingText={confirmationContent["heading"]}
          />

          <Modal isOpen={deliveryModal} toggle={toggleDeliveryModal}>
            <ModalBody className="px-4">
              <div className="d-flex justify-content-end">
                <CloseButton onClick={toggleDeliveryModal} className="fs-6" />
              </div>
              <div
                className="d-flex flex-column row-gap-2 align-items-center"
                style={{ marginTop: "-12px" }}
              >
                <h4 className="fw-bold">Delivery Estimate</h4>
                <h5 style={{ color: "#2C92E6" }} className="fw-bold">
                  {deliveryDate}
                </h5>
                <span className="text-secondary text-center">
                  This is only an estimate. Once you place the order, you will
                  be given an assured delivery date. For more details, please
                  reach out to support.
                </span>
              </div>
            </ModalBody>
          </Modal>

          <TabContent>
            <Outlet context={[currentOrder, setCurrentOrder, isAdmin]} />
          </TabContent>
        </div>
      )}
    </>
  );
};

export default Order;
